import { Box, Button, Card, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyServices } from '../../common/models/user.models';
import AvailabilityChip from './AvailabilityChip';
import ServiceConfigurationDialog from './ServiceConfigurationDialog';
import { ServiceConfiguration } from './state';

export interface ServiceCardProps {
  configuration: ServiceConfiguration;
  companyServices: CompanyServices;
  readOnly?: boolean;
  dense?: boolean;
}

export default function ServiceCard({ configuration, companyServices, readOnly, dense }: ServiceCardProps): React.JSX.Element {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['common', 'settings']);
  const { icon, wordingNamespace, service, serviceDetails } = configuration;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const denseDisplay = useMemo((): boolean => {
    return isXs || !!dense;
  }, [isXs, dense]);

  const isServiceConfigured = useMemo((): boolean => {
    return companyServices[service] !== null;
  }, [companyServices]);

  const values = useMemo((): string[] => {
    switch (service) {
      case 'provide_marketing':
        return companyServices.marketing_supports.map(({ support }) => support);
      case 'provide_return_policy':
        return companyServices.return_policy ? [companyServices.return_policy] : [];
      default:
        return [];
    }
  }, [companyServices]);

  const displayDetailsBlock = useMemo((): boolean => {
    return !!(companyServices[serviceDetails] || values.length);
  }, [companyServices, serviceDetails, values]);


  return (
    <>
      <Card
        className="Kheops-ServiceCard-card"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'surfaceContainerLow.main',
          p: 1.5,
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5, height: '100%' }}>
          <Box
            sx={{
              p: 0.5,
              borderRadius: 1,
              backgroundColor: 'surfaceContainer.main',
              height: {
                xs: 24,
                sm: 28,
              },
              'img': {
                height: {
                  xs: 24,
                  sm: 28,
                },
                width: {
                  xs: 24,
                  sm: 28,
                },
              },
            }}>
            <img src={icon} alt="" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
            <Typography variant='titleSmall'>{t(`settings:${wordingNamespace}.title`)}</Typography>
            <Typography
              variant={denseDisplay ? 'bodySmall' : 'bodyMedium'}
              color="onSurfaceVariant"
              sx={{
                flex: 1,
              }}
            >
              {t(`settings:${wordingNamespace}.description`)}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <AvailabilityChip value={companyServices[service]} readOnly={readOnly} />
            </Box>
          </Box>
          {!denseDisplay && !readOnly && (
            <Button
              variant={isServiceConfigured ? 'outlined' : 'contained'}
              onClick={() => setIsDialogOpen(true)}
            >
              {t(isServiceConfigured ? 'common:modify' : 'settings:configure')}
            </Button>
          )}
        </Box>
        {denseDisplay && !readOnly && (
          <Button
            variant={isServiceConfigured ? 'outlined' : 'contained'}
            onClick={() => setIsDialogOpen(true)}
            sx={{ alignSelf: 'end' }}
          >
            {t(isServiceConfigured ? 'common:modify' : 'settings:configure')}
          </Button>
        )}
        {displayDetailsBlock && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'outlineVariant.main',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {!!values.length && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="labelSmall">
                  {t(`settings:${wordingNamespace}.values.title`)} :
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {values.map((value) => (
                    <Chip
                      key={value}
                      variant="tag"
                      color="surfaceContainerHigh"
                      label={t(`settings:${wordingNamespace}.values.${value.toLocaleLowerCase()}`)}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {companyServices[serviceDetails] && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="labelSmall">
                  {t('common:details')} :
                </Typography>
                <Typography
                  variant="bodySmall"
                  sx={{
                    color: 'onSurfaceVariant.main',
                  }}
                >
                  {companyServices[serviceDetails]}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Card>
      {isDialogOpen && (
        <ServiceConfigurationDialog
          isOpen
          onClose={() => setIsDialogOpen(false)}
          configuration={configuration}
        />
      )}
    </>
  );
}
